import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { MapPin, Phone, Mail, Clock, Building, Send, CheckCircle, Loader2 } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Translations for Contact page
const pageText = {
  en: {
    hero: {
      title: 'Contact Us',
      subtitle: "We'd love to hear from you. Get in touch with our team today."
    },
    locations: {
      title: 'Our Locations',
      subtitle: 'Visit us at one of our convenient office locations',
      headOffice: 'Head Office',
      winnipeg: 'Winnipeg Office (Head Office)',
      edmonton: 'Edmonton Office'
    },
    form: {
      title: 'Send Us a Message',
      subtitle: 'Fill out the form below and one of our advisors will get back to you within 24 hours.',
      firstName: 'First Name',
      lastName: 'Last Name',
      email: 'Email Address',
      phone: 'Phone Number',
      service: 'Service Interested In',
      selectService: 'Select a service...',
      insurance: 'Insurance',
      investments: 'Investments',
      mortgage: 'Mortgage',
      other: 'Other',
      message: 'Your Message',
      messagePlaceholder: 'How can we help you?',
      submit: 'Send Message',
      sending: 'Sending...',
      success: {
        title: 'Message Sent!',
        subtitle: 'Thank you for reaching out. One of our advisors will get back to you within 24 hours.',
        button: 'Send Another Message'
      },
      error: 'Something went wrong. Please try again or contact us directly at hello@utmostfinancial.ca'
    },
    hours: {
      title: 'Business Hours',
      mondayFriday: 'Monday - Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
      weekdayHours: '9:00 AM - 5:00 PM',
      byAppointment: 'By Appointment',
      closed: 'Closed'
    },
    cta: {
      title: 'Ready to Get Started?',
      subtitle: 'Our team of experts is here to help you achieve your financial goals.',
      call: 'Call Us Now',
      email: 'Email Us'
    }
  },
  fr: {
    hero: {
      title: 'Contactez-Nous',
      subtitle: "Nous serions ravis de vous entendre. Contactez notre équipe aujourd'hui."
    },
    locations: {
      title: 'Nos Emplacements',
      subtitle: 'Visitez-nous à l\'un de nos bureaux pratiques',
      headOffice: 'Siège Social',
      winnipeg: 'Bureau de Winnipeg (Siège Social)',
      edmonton: 'Bureau d\'Edmonton'
    },
    form: {
      title: 'Envoyez-Nous un Message',
      subtitle: 'Remplissez le formulaire ci-dessous et l\'un de nos conseillers vous répondra dans les 24 heures.',
      firstName: 'Prénom',
      lastName: 'Nom de Famille',
      email: 'Adresse Courriel',
      phone: 'Numéro de Téléphone',
      service: 'Service Qui Vous Intéresse',
      selectService: 'Sélectionnez un service...',
      insurance: 'Assurance',
      investments: 'Investissements',
      mortgage: 'Hypothèque',
      other: 'Autre',
      message: 'Votre Message',
      messagePlaceholder: 'Comment pouvons-nous vous aider?',
      submit: 'Envoyer le Message',
      sending: 'Envoi en cours...',
      success: {
        title: 'Message Envoyé!',
        subtitle: 'Merci de nous avoir contactés. L\'un de nos conseillers vous répondra dans les 24 heures.',
        button: 'Envoyer un Autre Message'
      },
      error: 'Une erreur s\'est produite. Veuillez réessayer ou nous contacter directement à hello@utmostfinancial.ca'
    },
    hours: {
      title: 'Heures d\'Ouverture',
      mondayFriday: 'Lundi - Vendredi',
      saturday: 'Samedi',
      sunday: 'Dimanche',
      weekdayHours: '9h00 - 17h00',
      byAppointment: 'Sur Rendez-vous',
      closed: 'Fermé'
    },
    cta: {
      title: 'Prêt à Commencer?',
      subtitle: 'Notre équipe d\'experts est là pour vous aider à atteindre vos objectifs financiers.',
      call: 'Appelez-Nous',
      email: 'Écrivez-Nous'
    }
  }
};

const ContactPage = () => {
  const { language } = useLanguage();
  const text = pageText[language];
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone || null,
          subject: formData.service || 'General Inquiry',
          message: formData.message
        }),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', service: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const offices = [
    {
      id: 1,
      name: language === 'fr' ? 'Bureau de Winnipeg (Siège Social)' : 'Winnipeg Office (Head Office)',
      address: 'A-970 Sargent Avenue',
      city: 'Winnipeg, MB R3E 0E3',
      phone: '1-877-239-9060',
      email: 'hello@utmostfinancial.ca',
      isHeadOffice: true
    },
    {
      id: 2,
      name: language === 'fr' ? 'Bureau d\'Edmonton' : 'Edmonton Office',
      address: '5420 93A Avenue NW',
      city: 'Edmonton, AB T6B 2K7',
      phone: '1-877-239-9060',
      email: 'hello@utmostfinancial.ca',
      isHeadOffice: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[350px] flex items-center justify-center overflow-hidden bg-[#0077B6]">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0077B6] to-[#005A8C]" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3 text-white">{text.hero.title}</h1>
          <p className="text-lg text-blue-100">
            {text.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.locations.title}</h2>
            <p className="text-gray-600">{text.locations.subtitle}</p>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mt-4"></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {offices.map((office) => (
              <Card key={office.id} className={`bg-white border hover:shadow-xl transition-shadow ${office.isHeadOffice ? 'border-2 border-[#D4AF37]' : 'border-gray-200'}`}>
                <CardContent className="p-8">
                  <div className="flex items-start mb-4">
                    <Building className="h-8 w-8 text-[#0077B6] mr-3 flex-shrink-0" />
                    <div>
                      <h3 className="text-xl font-bold text-gray-800">{office.name}</h3>
                      {office.isHeadOffice && (
                        <span className="inline-block bg-[#D4AF37] text-[#003D5C] text-xs px-2 py-1 rounded mt-1 font-semibold">{text.locations.headOffice}</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="space-y-4 mt-6">
                    <div className="flex items-start">
                      <MapPin className="h-5 w-5 text-[#0077B6] mr-3 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-gray-700">{office.address}</p>
                        <p className="text-gray-700">{office.city}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <Phone className="h-5 w-5 text-[#0077B6] mr-3 flex-shrink-0" />
                      <a href={`tel:${office.phone.replace(/-/g, '')}`} className="text-[#0077B6] hover:underline font-medium">
                        {office.phone}
                      </a>
                    </div>
                    
                    <div className="flex items-center">
                      <Mail className="h-5 w-5 text-[#0077B6] mr-3 flex-shrink-0" />
                      <a href={`mailto:${office.email}`} className="text-[#0077B6] hover:underline font-medium">
                        {office.email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-[#F0F7FF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.form.title}</h2>
            <p className="text-gray-600">
              {text.form.subtitle}
            </p>
          </div>

          <Card className="shadow-lg bg-white border border-gray-200">
            <CardContent className="p-8">
              {submitStatus === 'success' ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-10 w-10 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">{text.form.success.title}</h3>
                  <p className="text-gray-600 mb-6">
                    {text.form.success.subtitle}
                  </p>
                  <Button 
                    onClick={() => setSubmitStatus(null)}
                    className="bg-[#0077B6] hover:bg-[#005A8C]"
                  >
                    {text.form.success.button}
                  </Button>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                    {text.form.error}
                  </div>
                )}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                      {text.form.firstName} *
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800 placeholder-gray-400"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                      {text.form.lastName} *
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800 placeholder-gray-400"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      {text.form.email} *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800 placeholder-gray-400"
                      placeholder="john.doe@example.com"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      {text.form.phone}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800 placeholder-gray-400"
                      placeholder="(204) 555-0123"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                    {text.form.service}
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all text-gray-800"
                  >
                    <option value="">{text.form.selectService}</option>
                    <option value="Insurance">{text.form.insurance}</option>
                    <option value="Investments">{text.form.investments}</option>
                    <option value="Mortgage">{text.form.mortgage}</option>
                    <option value="Other">{text.form.other}</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    {text.form.message} *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all resize-none text-gray-800 placeholder-gray-400"
                    placeholder={text.form.messagePlaceholder}
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  disabled={isSubmitting}
                  className="w-full bg-[#0077B6] hover:bg-[#005A8C] text-white font-semibold py-4 disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                      {text.form.sending}
                    </>
                  ) : (
                    <>
                      <Send className="h-5 w-5 mr-2" />
                      {text.form.submit}
                    </>
                  )}
                </Button>
              </form>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Business Hours */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-800 mb-3">{text.hours.title}</h2>
            <Clock className="h-12 w-12 text-[#D4AF37] mx-auto mt-4" />
          </div>

          <div className="max-w-md mx-auto">
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-600">{text.hours.mondayFriday}</span>
                <span className="font-semibold text-gray-800">{text.hours.weekdayHours}</span>
              </div>
              <div className="flex justify-between items-center py-3 border-b border-gray-200">
                <span className="text-gray-600">{text.hours.saturday}</span>
                <span className="font-semibold text-gray-800">{text.hours.byAppointment}</span>
              </div>
              <div className="flex justify-between items-center py-3">
                <span className="text-gray-600">{text.hours.sunday}</span>
                <span className="font-semibold text-gray-800">{text.hours.closed}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-[#0077B6]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3 text-white">{text.cta.title}</h2>
          <p className="text-blue-100 mb-6">
            {text.cta.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:18772399060" 
              className="inline-flex items-center justify-center bg-[#D4AF37] text-[#003D5C] hover:bg-[#B8962E] font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              <Phone className="h-5 w-5 mr-2" />
              {text.cta.call}
            </a>
            <a 
              href="mailto:hello@utmostfinancial.ca" 
              className="inline-flex items-center justify-center border-2 border-white text-white hover:bg-white hover:text-[#0077B6] font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              <Mail className="h-5 w-5 mr-2" />
              {text.cta.email}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
